import React from 'react';
import { useHistory } from 'react-router-dom';
import packageJson from '../../../package.json';
import {
    AccountMenuItemButton,
    AppBarLayout,
    AppSwitcherAccountMenuItem,
    AppSwitcherPanel,
    EnvironmentInfo,
    NavigationItem,
    NeonAccountLink,
    NeonSignOutLink,
    NeonSupportCenterLink,
    SectionDivider,
    SectionSubheader,
    ThemeToggle,
    UserInfo,
    applyAppSwitcherItemUrls,
    defaultAppSwitcherItems,
    getAppSwitcherConfigUrls,
    useAppLayout,
} from '@mds/app-layout';
import { Box, IconButton, Tooltip } from '@mui/material';
import MagnifyingGlass from '@mds/icons/MagnifyingGlass';
import SlidersHorizontal from '@mds/icons/SlidersHorizontal';
import { config } from '../../config';
import { baseUrls } from '../../utils/baseUrls.utils';
import { CurrentUserDetails } from '../../shared/types';
import { InsightsLogoMark } from './InsightsLogoMark';

const { version } = packageJson;

export interface ResearchAppBarProps {
    userDetails?: CurrentUserDetails;
    menuItems?: {
        name: string;
        link: string;
        isActive?: boolean;
    }[];
    accessToken?: string;
    onSignOut: () => void;
}

const SearchButton = (props: { compact: boolean }) => {
    const history = useHistory();

    return (
        <Tooltip title="Search">
            <IconButton
                id="app-header-search-button"
                color="neutral"
                size={props.compact ? 'small' : 'medium'}
                onClick={() => history.push(baseUrls.search)}
                aria-label="Search"
            >
                <MagnifyingGlass />
            </IconButton>
        </Tooltip>
    );
};

// Desktop keeps the app-bar trigger; `appSwitcherPlacement="responsive"` (the default)
// moves it into the account menu on compact/mobile layouts once `AppSwitcherAccountMenuItem`
// is present there. No hardcoded URLs: getAppSwitcherConfigUrls() reads
// window.mdsAppSwitcherConfig/window._env, the same runtime config every Neon app already sets.
const items = applyAppSwitcherItemUrls(
    defaultAppSwitcherItems,
    getAppSwitcherConfigUrls() ?? {}
);

const InsightsAppSwitcherPanel = (props: { accessToken?: string }) => {
    const { appBarVariant } = useAppLayout();

    return (
        <AppSwitcherPanel
            items={items}
            isMobile={appBarVariant === 'compact'}
            appName="insights"
            accessToken={props.accessToken}
        />
    );
};

const ResearchAppBar = (props: ResearchAppBarProps) => {
    const history = useHistory();

    const manageProfileLink = config.landingPageUrl + '/manage-account/profile';
    const supportCenterLink = config.landingPageUrl + '/support';
    const versionText = version.replace('-', '.');

    const accountMenuItems = (
        <>
            <UserInfo user={props.userDetails} />
            <ThemeToggle />
            <NeonAccountLink url={manageProfileLink} />
            <NeonSupportCenterLink url={supportCenterLink} />
            <AppSwitcherAccountMenuItem />
            <SectionDivider />
            <SectionSubheader>This app</SectionSubheader>
            <AccountMenuItemButton
                icon={<SlidersHorizontal />}
                onClick={() => {
                    history.push(baseUrls.emailNotifications);
                }}
            >
                Manage email notifications
            </AccountMenuItemButton>
            <SectionDivider />
            <NeonSignOutLink logout={props.onSignOut} />
            <EnvironmentInfo
                deployEnv={config.deployEnv}
                appVersion={versionText}
            />
        </>
    );

    const navigationItems = props.menuItems?.map((menuItem) => (
        <NavigationItem
            key={menuItem.link}
            to={menuItem.link}
            exact
            active={menuItem.isActive}
        >
            {menuItem.name}
        </NavigationItem>
    ));

    return (
        <Box id="app-header" sx={{ '@media print': { display: 'none' } }}>
            <AppBarLayout
                logo={<InsightsLogoMark />}
                title="Insights"
                user={props.userDetails}
                homePageLinkProps={{ to: baseUrls.forYou }}
                navigationItems={navigationItems}
                accountMenuItems={accountMenuItems}
                appSwitcherItems={
                    <InsightsAppSwitcherPanel accessToken={props.accessToken} />
                }
                actions={({ compact }: { compact: boolean }) => (
                    <SearchButton compact={compact} />
                )}
            />
        </Box>
    );
};

export default ResearchAppBar;
